import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { FlexBox } from '../box';
import { Skeleton } from '../skeleton';
import { styled } from '../styled';
import { Typography } from '../typography';
import { withVersion } from '../utils';
const Digit = styled('input', {
    shouldForwardProp: (prop) => prop !== '$error'
})(({ theme, $error }) => ({
    width: 56,
    height: 56,
    padding: 0,
    textAlign: 'center',
    fontSize: 24,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    caretColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${$error ? theme.palette.error.main : theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    outline: 'none',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    boxSizing: 'border-box',
    '&:hover:not(:disabled)': {
        borderColor: $error
            ? theme.palette.error.main
            : theme.palette.text.primary
    },
    '&:focus': {
        borderColor: $error
            ? theme.palette.error.main
            : theme.palette.primary.main,
        boxShadow: `0 0 0 1px ${$error ? theme.palette.error.main : theme.palette.primary.main}`
    },
    '&:disabled': {
        color: theme.palette.text.disabled,
        backgroundColor: theme.palette.action.disabledBackground,
        cursor: 'not-allowed'
    }
}));
function sanitize(raw, inputMode) {
    return inputMode === 'numeric' ? raw.replace(/\D/g, '') : raw.trim();
}
let ID_COUNTER = 0;
const getNextId = (prefix) => `${prefix}-${(ID_COUNTER += 1)}`;
const VerificationCodeFieldImpl = React.forwardRef((props, ref) => {
    const { length = 5, value = '', onChange, onComplete, error, helperText, label, required, disabled, skeleton, autoFocus, inputMode = 'numeric', autoComplete = 'one-time-code', className, 'aria-label': ariaLabel, 'data-role': dataRole = 'verification-code-field', 'data-role-value': dataRoleValue } = props;
    const hasError = Boolean(error);
    const errorMessage = typeof error === 'boolean' ? undefined : error;
    const helperContent = errorMessage ?? helperText;
    const stateColor = disabled
        ? 'text.disabled'
        : hasError
            ? 'error.main'
            : 'text.secondary';
    const helperId = React.useRef(getNextId('verification-code-helper')).current;
    const labelId = React.useRef(getNextId('verification-code-label')).current;
    const inputsRef = React.useRef([]);
    const digits = React.useMemo(() => {
        const chars = sanitize(value, inputMode).slice(0, length).split('');
        return Array.from({ length }, (_, index) => chars[index] ?? '');
    }, [value, length, inputMode]);
    const focusInput = (index) => {
        const clamped = Math.max(0, Math.min(index, length - 1));
        inputsRef.current[clamped]?.focus();
        inputsRef.current[clamped]?.select();
    };
    const emit = (next) => {
        const combined = next.join('');
        const wasComplete = !digits.includes('');
        onChange?.(combined);
        if (!wasComplete && combined.length === length && !next.includes('')) {
            onComplete?.(combined);
        }
    };
    const handleChange = (index, event) => {
        const sanitized = sanitize(event.target.value, inputMode);
        if (!sanitized) {
            const next = [...digits];
            next[index] = '';
            emit(next);
            return;
        }
        const next = [...digits];
        const chars = sanitized.split('');
        let cursor = index;
        for (const char of chars) {
            if (cursor >= length) {
                break;
            }
            next[cursor] = char;
            cursor += 1;
        }
        emit(next);
        focusInput(cursor);
    };
    const handleKeyDown = (index, event) => {
        switch (event.key) {
            case 'Backspace': {
                event.preventDefault();
                const next = [...digits];
                if (next[index]) {
                    next[index] = '';
                    emit(next);
                }
                else if (index > 0) {
                    next[index - 1] = '';
                    emit(next);
                    focusInput(index - 1);
                }
                break;
            }
            case 'Delete': {
                event.preventDefault();
                const next = [...digits];
                next[index] = '';
                emit(next);
                break;
            }
            case 'ArrowLeft':
                event.preventDefault();
                focusInput(index - 1);
                break;
            case 'ArrowRight':
                event.preventDefault();
                focusInput(index + 1);
                break;
            default:
                break;
        }
    };
    const handlePaste = (index, event) => {
        event.preventDefault();
        const pasted = sanitize(event.clipboardData.getData('text'), inputMode);
        if (!pasted) {
            return;
        }
        const next = [...digits];
        let cursor = index;
        for (const char of pasted.split('')) {
            if (cursor >= length) {
                break;
            }
            next[cursor] = char;
            cursor += 1;
        }
        emit(next);
        focusInput(cursor);
    };
    return (_jsxs(FlexBox, { stack: true, gap: 1, className: className, "data-role": dataRole, "data-role-value": dataRoleValue, children: [label && (_jsxs(Typography, { id: labelId, color: stateColor, children: [label, required && ' *'] })), _jsx(FlexBox, { gap: 2, role: "group", "aria-labelledby": label ? labelId : undefined, "aria-label": label ? undefined : ariaLabel, "aria-describedby": helperContent ? helperId : undefined, "data-role": `${dataRole}-group`, children: skeleton
                    ? Array.from({ length }, (_, index) => (_jsx(Skeleton, { variant: "rectangular", width: 56, height: 56, "data-role": `${dataRole}-skeleton`, "data-role-value": String(index) }, index)))
                    : digits.map((digit, index) => (_jsx(Digit, { ref: (node) => {
                            inputsRef.current[index] = node;
                            if (index === 0 &&
                                typeof ref === 'function') {
                                ref(node);
                            }
                            else if (index === 0 && ref) {
                                ref.current = node;
                            }
                        }, type: inputMode === 'numeric' ? 'tel' : 'text', inputMode: inputMode, autoComplete: index === 0 ? autoComplete : 'off', maxLength: 1, value: digit, disabled: disabled, autoFocus: autoFocus && index === 0, "$error": hasError, "aria-label": `${ariaLabel ?? 'Digit'} ${index + 1}`, "aria-invalid": hasError || undefined, "aria-required": required || undefined, onChange: (event) => handleChange(index, event), onKeyDown: (event) => handleKeyDown(index, event), onPaste: (event) => handlePaste(index, event), onFocus: (event) => event.target.select(), "data-role": `${dataRole}-input`, "data-role-value": String(index) }, index))) }), helperContent && (_jsx(Typography, { id: helperId, variant: "detail", color: stateColor, children: helperContent }))] }));
});
VerificationCodeFieldImpl.displayName =
    'VerificationCodeField';
export const VerificationCodeField = withVersion(VerificationCodeFieldImpl, '1.0.0');
